import React, { useState, useEffect, useCallback } from 'react';
import { db, storage } from '../../firebase';
import { collection, getDocs, doc, updateDoc } from 'firebase/firestore';
import { ref, getDownloadURL } from 'firebase/storage';
import { seedDemoData } from '../../utils/seedDemoData';

function AdminClubs() {
  const [clubs, setClubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [updating, setUpdating] = useState(null);
  const [seeding, setSeeding] = useState(false);
  const [seedResult, setSeedResult] = useState(null);

  const loadClubs = useCallback(async () => {
    setLoading(true);
    try {
      const snap = await getDocs(collection(db, 'clubs'));
      const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      list.sort((a, b) => {
        const order = { pending: 0, approved: 1, rejected: 2 };
        const aVal = order[a.w9Status] ?? 3;
        const bVal = order[b.w9Status] ?? 3;
        if (aVal !== bVal) return aVal - bVal;
        return (a.name || '').localeCompare(b.name || '');
      });
      setClubs(list);
    } catch (err) {
      console.error('[AdminClubs] Load error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadClubs(); }, [loadClubs]);

  const handleW9StatusChange = async (clubId, newStatus) => {
    setUpdating(clubId);
    try {
      await updateDoc(doc(db, 'clubs', clubId), { w9Status: newStatus });
      setClubs((prev) => prev.map((c) => c.id === clubId ? { ...c, w9Status: newStatus } : c));
    } catch (err) {
      console.error('[AdminClubs] Status update error:', err);
    } finally {
      setUpdating(null);
    }
  };

  const handleViewW9 = async (club) => {
    if (!club.w9StoragePath) return;
    try {
      const url = await getDownloadURL(ref(storage, club.w9StoragePath));
      window.open(url, '_blank');
    } catch (err) {
      console.error('[AdminClubs] W-9 download error:', err);
      alert('Could not load W-9 file. It may have been deleted.');
    }
  };

  const filtered = clubs.filter((c) => {
    if (filter === 'all') return true;
    if (filter === 'none') return !c.w9Status;
    return c.w9Status === filter;
  });

  const counts = {
    all: clubs.length,
    pending: clubs.filter((c) => c.w9Status === 'pending').length,
    approved: clubs.filter((c) => c.w9Status === 'approved').length,
    rejected: clubs.filter((c) => c.w9Status === 'rejected').length,
    none: clubs.filter((c) => !c.w9Status).length,
  };

  return (
    <div className="admin-clubs card card--wide">
      <h2 className="admin-settings-title">Clubs</h2>

      {/* TEMP: Seed demo data button */}
      <div className="admin-settings-section" style={{ marginBottom: '24px' }}>
        <div className="admin-settings-row">
          <div className="admin-settings-label-wrap">
            <span className="admin-settings-label">Seed Demo Data</span>
            <span className="admin-settings-hint text-secondary text-sm">
              Creates ~36 demo players and 6 months of stats for Oakdale Rockets.
            </span>
          </div>
          <button
            type="button"
            className="btn btn--primary"
            onClick={async () => {
              setSeeding(true);
              setSeedResult(null);
              try {
                const result = await seedDemoData();
                setSeedResult(`Done — ${result.playersCreated} players, ${result.monthlyStats} months`);
                loadClubs();
              } catch (err) {
                setSeedResult(`Error: ${err.message}`);
              } finally {
                setSeeding(false);
              }
            }}
            disabled={seeding}
          >
            {seeding ? 'Seeding...' : 'Seed Now'}
          </button>
        </div>
        {seedResult && (
          <p className="text-secondary text-sm" style={{ marginTop: '8px' }}>{seedResult}</p>
        )}
      </div>

      <div className="admin-clubs-filters">
        {[
          { key: 'all', label: 'All' },
          { key: 'pending', label: 'Pending' },
          { key: 'approved', label: 'Approved' },
          { key: 'rejected', label: 'Rejected' },
          { key: 'none', label: 'No W-9' },
        ].map(({ key, label }) => (
          <button
            key={key}
            type="button"
            className={`admin-clubs-filter-btn ${filter === key ? 'active' : ''}`}
            onClick={() => setFilter(key)}
          >
            {label} ({counts[key]})
          </button>
        ))}
      </div>

      {loading ? (
        <p className="text-secondary" style={{ padding: '24px 0' }}>Loading clubs…</p>
      ) : filtered.length === 0 ? (
        <p className="text-secondary" style={{ padding: '24px 0' }}>No clubs match this filter.</p>
      ) : (
        <div className="admin-clubs-list">
          {filtered.map((club) => (
            <div key={club.id} className="admin-clubs-row">
              <div className="admin-clubs-info">
                <strong className="admin-clubs-name">{club.name || 'Unnamed Club'}</strong>
                <span className="text-secondary text-sm">{club.slug ? `/clubs/${club.slug}` : club.id}</span>
              </div>

              <div className="admin-clubs-w9">
                <span className={`admin-clubs-badge ${club.w9Status || 'none'}`}>
                  {club.w9Status || 'No W-9'}
                </span>

                {club.w9StoragePath && (
                  <button
                    type="button"
                    className="btn btn--ghost btn--sm"
                    onClick={() => handleViewW9(club)}
                  >
                    View W-9
                  </button>
                )}

                {club.w9StoragePath && (
                  <div className="admin-clubs-actions">
                    {club.w9Status !== 'approved' && (
                      <button
                        type="button"
                        className="btn btn--sm admin-clubs-approve"
                        onClick={() => handleW9StatusChange(club.id, 'approved')}
                        disabled={updating === club.id}
                      >
                        Approve
                      </button>
                    )}
                    {club.w9Status !== 'rejected' && (
                      <button
                        type="button"
                        className="btn btn--sm admin-clubs-reject"
                        onClick={() => handleW9StatusChange(club.id, 'rejected')}
                        disabled={updating === club.id}
                      >
                        Reject
                      </button>
                    )}
                  </div>
                )}
              </div>

              <div className="admin-clubs-meta text-secondary text-sm">
                <span>Markup: ${club.markupPerMonth ?? 0}/mo</span>
                <span>Stripe: {club.stripeAccountId ? 'Connected' : 'None'}</span>
                <span>Teams: {club.teams?.length ?? 0}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default AdminClubs;
